import {
    AcademicCapIcon,
    BriefcaseIcon,
    BuildingLibraryIcon,
    BuildingOffice2Icon,
    BuildingStorefrontIcon,
    CubeIcon,
    HeartIcon,
    HomeModernIcon,
    ShoppingBagIcon,
    SparklesIcon,
    TruckIcon,
    UserGroupIcon,
    WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType } from "react";

/**
 * Single source of truth for the floGPT vertical-pack catalogue surfaced
 * in marketing UI: `/use-cases` index, the per-industry landing pages
 * (`/use-cases/:slug`), and the navbar mega menu. Keys here mirror
 * `backend/src/seeds/callWidgetTemplates/*.json` so onboarding stays in
 * sync with what the website promises.
 */

export type WidgetType = "lead_generation" | "appointment_booking" | "customer_support";
export type SpeakerRole = "AI" | "User";
export type DemoMode = "voice" | "chat";

export interface ScriptTurn {
    role: SpeakerRole;
    text: string;
}

export interface RecommendedTrigger {
    name: string;
    description: string;
}

export interface IndustryUseCase {
    /** Stable identifier (matches seed JSON `key`). */
    key: string;
    /** URL-friendly slug — `key` with `_` → `-`. */
    slug: string;
    /** Long form label, fits the per-page hero. */
    label: string;
    /** Compact label, fits a navbar item or small card. */
    shortLabel: string;
    /** Use case classification — drives the colored pill. */
    callWidgetType: WidgetType;
    /** Default language locale used in the showcase. */
    language: string;
    /** Lucide/Heroicon component for badges & cards. */
    Icon: ComponentType<{ className?: string }>;
    /** Tailwind gradient stops, e.g. "from-orange-500 to-red-500". */
    accent: string;
    /** Tailwind soft glow class, e.g. "bg-orange-500/15". */
    softAccent: string;
    /** Short marketing tagline (1 sentence) for cards. */
    tagline: string;
    /** Long description used on the per-industry hero. */
    description: string;
    /**
     * Optional hero H1. When omitted, the page renders
     * "AI Voice + Chat for {shortLabel}". Set only to replace that line;
     * shortLabel still drives nav, cards, and section headings.
     */
    headline?: string;
    /** 4-5 bullet points of "what FloGPT does for {industry}". */
    features: string[];
    /** "Why FloGPT" — 3 short reasons specific to the vertical. */
    whyFloGPT: Array<{ title: string; description: string }>;
    /** Animated voice script (5 turns, kept short for snappy demo). */
    voiceScript: ScriptTurn[];
    /** Animated chat script (5 turns). */
    chatScript: ScriptTurn[];
    /** 3-4 step pipeline shown as a footer chip row. */
    pipelineSteps: string[];
    /** Pre-built outbound triggers shipped with the seed pack. */
    recommendedTriggers: RecommendedTrigger[];
    /** SEO metadata. */
    seo: {
        title: string;
        description: string;
        keywords: string[];
    };
}

export const WIDGET_TYPE_LABEL: Record<WidgetType, string> = {
    lead_generation: "Lead generation",
    appointment_booking: "Appointment booking",
    customer_support: "Customer support",
};

export const WIDGET_TYPE_BADGE: Record<WidgetType, string> = {
    lead_generation: "from-orange-500 to-pink-500",
    appointment_booking: "from-emerald-500 to-teal-500",
    customer_support: "from-blue-500 to-indigo-500",
};

const slug = (key: string) => key.replace(/_/g, "-");

export const INDUSTRY_USE_CASES: IndustryUseCase[] = [
    {
        key: "auto_dealers",
        slug: slug("auto_dealers"),
        label: "Auto Dealers",
        shortLabel: "Auto Dealers",
        callWidgetType: "lead_generation",
        language: "English (US)",
        Icon: TruckIcon,
        accent: "from-orange-500 to-red-500",
        softAccent: "bg-orange-500/15",
        tagline: "Capture every test-drive and trade-in lead — even after hours.",
        description:
            "FloGPT picks up every showroom call, qualifies the buyer's vehicle of interest, captures trade-in details, and hands a warm lead to your sales rep with a follow-up SMS — without missing a service-department transfer.",
        features: [
            "Qualify vehicle interest, financing intent and trade-in details on every call",
            "Send the matching VDP link by SMS while the caller is still on the line",
            "Push leads to DealerSocket, VinSolutions or any CRM via webhook",
            "Route service-department callers to the service team with warm transfer",
            "Schedule test drives directly into your dealership calendar",
        ],
        whyFloGPT: [
            { title: "Never miss a buyer", description: "Pick up after hours, on weekends, and during sales spikes — no missed inventory inquiries." },
            { title: "Qualified, not raw", description: "Every lead arrives with vehicle interest, trade-in, financing intent and timeline already captured." },
            { title: "Salesperson-friendly", description: "Calls end with the right human looped in, briefed, and the SMS already sent — no double work." },
        ],
        voiceScript: [
            { role: "AI", text: "Sunrise Motors, you're speaking with Aria. New vehicle, service, or trade-in?" },
            { role: "User", text: "I'd like to test drive the new Outback this Saturday." },
            { role: "AI", text: "I have 11 AM, 2 PM, or 4 PM. Which works best?" },
            { role: "User", text: "2 PM. And I have a 2018 Civic to trade in." },
            { role: "AI", text: "Booked. Trade-in form and directions are on their way by SMS." },
        ],
        chatScript: [
            { role: "User", text: "Any used Camrys under $25k?" },
            { role: "AI", text: "Three on the lot — 2021 LE, 2020 SE, 2022 hybrid. Want links?" },
            { role: "User", text: "Send the hybrid." },
            { role: "AI", text: "Sent. Want to schedule a test drive while you're here?" },
            { role: "User", text: "Yes — Saturday afternoon." },
        ],
        pipelineSteps: ["Greet", "Qualify", "Book", "SMS"],
        recommendedTriggers: [
            { name: "Showroom-visit follow-up", description: "Call leads who walked the lot but didn't buy, 24 hours later." },
            { name: "Service-due reminder", description: "Place a courtesy call when a customer is due for service or recall." },
        ],
        seo: {
            title: "AI Voice & Chat for Auto Dealers — FloGPT",
            description:
                "Auto-dealer AI agent for inbound calls and live chat. Qualifies test-drive leads, captures trade-ins, books appointments and pushes leads into DealerSocket / VinSolutions automatically.",
            keywords: ["AI for car dealerships", "auto dealer chatbot", "dealership AI voice agent", "test drive booking AI"],
        },
    },
    {
        key: "dental_healthcare",
        slug: slug("dental_healthcare"),
        label: "Dental & Medical / Healthcare",
        shortLabel: "Dental & Healthcare",
        callWidgetType: "appointment_booking",
        language: "English (IN)",
        Icon: HeartIcon,
        accent: "from-rose-500 to-pink-500",
        softAccent: "bg-rose-500/15",
        tagline: "Book the appointment, route the emergency, never give medical advice.",
        description:
            "A polite, empathetic booking assistant that confirms names and dates back, never diagnoses, and instantly transfers callers who mention chest pain, severe bleeding or any urgent symptom — with HIPAA-aware logging and EHR webhooks built in.",
        features: [
            "Book, reschedule and cancel patient appointments 24/7",
            "Verify insurance and surface in-network status during the call or chat",
            "Send appointment-confirmation SMS and 24-hour reminder calls automatically",
            "Escalate emergencies (bleeding, chest pain, breathing) to the on-call line instantly",
            "Push every booking to your EHR via webhook with audit log",
        ],
        whyFloGPT: [
            { title: "Empathetic, never clinical", description: "Trained to never diagnose or recommend treatment — defers to the clinical team for any medical question." },
            { title: "Built for compliance", description: "HIPAA-aware audit logs, transcript retention controls, and call recording opt-in." },
            { title: "Never a missed booking", description: "Captures the appointment even when your front desk is on another line or the practice is closed." },
        ],
        voiceScript: [
            { role: "AI", text: "Acme Dental, this is the booking assistant. How can I help?" },
            { role: "User", text: "I'd like to book a cleaning Friday afternoon." },
            { role: "AI", text: "I have 2:30 PM or 4:15 PM Friday. Which do you prefer?" },
            { role: "User", text: "4:15 please." },
            { role: "AI", text: "Booked with Dr. Chen. Confirmation SMS on its way." },
        ],
        chatScript: [
            { role: "User", text: "Do you take Cigna PPO insurance?" },
            { role: "AI", text: "Yes — in-network with Cigna PPO. Want me to verify your benefits?" },
            { role: "User", text: "Yes, member ID 88231-A." },
            { role: "AI", text: "Verifying… preventive care fully covered, $50 copay for fillings." },
            { role: "User", text: "Great, can I book a cleaning?" },
        ],
        pipelineSteps: ["Greet", "Verify", "Book", "Confirm"],
        recommendedTriggers: [
            { name: "Appointment reminder (24h before)", description: "Calls the patient 24 hours before their booked appointment to confirm." },
            { name: "SMS reminder (2h before)", description: "Texts the patient 2 hours before their appointment with the time and reschedule link." },
            { name: "No-show callback", description: "Calls the patient 30 minutes after a missed appointment slot to reschedule." },
        ],
        seo: {
            title: "AI Voice & Chat for Dental & Healthcare — FloGPT",
            description:
                "Dental and medical AI assistant that books appointments 24/7, verifies insurance, sends SMS reminders, and instantly escalates emergencies — with HIPAA-aware logs and EHR webhooks.",
            keywords: ["dental AI receptionist", "healthcare appointment booking AI", "medical practice chatbot", "HIPAA AI agent"],
        },
    },
    {
        key: "education",
        slug: slug("education"),
        label: "Education",
        shortLabel: "Education",
        callWidgetType: "lead_generation",
        language: "English (IN)",
        Icon: AcademicCapIcon,
        accent: "from-purple-500 to-fuchsia-500",
        softAccent: "bg-purple-500/15",
        tagline: "Convert prospective students into booked counselor calls — automatically.",
        description:
            "An admissions assistant that captures program interest, education level, and start date for every inbound enquiry, then emails the brochure and pushes the lead into Slate or Salesforce EDU — never promising admission.",
        headline: "AI Admissions Agent for Colleges & Schools",
        features: [
            "Capture program of interest, education level, planned start date and financing questions",
            "Email program brochures the moment a prospective student asks",
            "Push enquiries to Slate, Salesforce EDU or any admissions CRM",
            "Book counselor calls and campus tours into your team calendar",
            "Operate in 16 languages — English, Hindi and 9 Indic locales out of the box",
        ],
        whyFloGPT: [
            { title: "Always warm, never pushy", description: "Trained to be encouraging and informative — never to promise admission or financial aid amounts." },
            { title: "Multilingual by default", description: "Greet international applicants in their language, with auto-detection across 16 locales." },
            { title: "Counselor handover", description: "Hands the conversation to your admissions team with full context — no repeat questions." },
        ],
        voiceScript: [
            { role: "AI", text: "Hi, exploring our programs or following up on an application?" },
            { role: "User", text: "Thinking about the data-science bootcamp." },
            { role: "AI", text: "Great — looking at the 12-week or 6-month track?" },
            { role: "User", text: "12-week. When does the next cohort start?" },
            { role: "AI", text: "March 4. I'll text the brochure and a counselor will call back today." },
        ],
        chatScript: [
            { role: "User", text: "How much is the bootcamp?" },
            { role: "AI", text: "$9,800 with income-share-agreement options. Want financing details?" },
            { role: "User", text: "Yes please." },
            { role: "AI", text: "Sent. Want to talk to a counselor — today or tomorrow?" },
            { role: "User", text: "Tomorrow morning." },
        ],
        pipelineSteps: ["Greet", "Qualify", "Brochure", "CRM"],
        recommendedTriggers: [
            { name: "Application-deadline reminder", description: "Calls applicants two days before the deadline to nudge them to submit." },
            { name: "Open-house RSVP follow-up", description: "Confirms attendance the morning of the open house and re-routes no-shows." },
        ],
        seo: {
            title: "AI Admissions Agent for Colleges & Schools | FloGPT",
            description:
                "AI voice and chat for admissions: capture inquiries, send brochures, book counselor calls, and sync to Slate or Salesforce EDU — in 16 languages.",
            keywords: ["AI admissions assistant", "education chatbot", "student recruitment AI", "bootcamp lead AI"],
        },
    },
    {
        key: "fitness_wellness",
        slug: slug("fitness_wellness"),
        label: "Fitness & Wellness",
        shortLabel: "Fitness & Wellness",
        callWidgetType: "appointment_booking",
        language: "English (IN)",
        Icon: SparklesIcon,
        accent: "from-emerald-500 to-teal-500",
        softAccent: "bg-emerald-500/15",
        tagline: "Book classes and personal-training sessions while your front desk runs the floor.",
        description:
            "A friendly studio receptionist for gyms, yoga and wellness studios — capturing the desired service, preferred trainer and goals, mentioning your cancellation policy, and never giving medical or nutrition advice.",
        features: [
            "Book group classes, personal-training sessions and studio tours",
            "Mention your cancellation policy on every booking automatically",
            "Push bookings to Mindbody, ClubReady or any calendar via webhook",
            "Match callers with the right trainer based on goals and availability",
            "Send SMS confirmations and class reminders without front-desk effort",
        ],
        whyFloGPT: [
            { title: "Stays in lane", description: "Defers all medical and nutrition questions to a trainer — keeps you out of liability territory." },
            { title: "Designed for studio rush hours", description: "Picks up the line during peak class times so you keep your members happy on the floor." },
            { title: "Membership-aware", description: "Recognises returning members and skips repeat questions for a smoother experience." },
        ],
        voiceScript: [
            { role: "AI", text: "Hi! Booking a class, personal training, or asking about membership?" },
            { role: "User", text: "Personal training, twice a week with Maya." },
            { role: "AI", text: "Maya has Mon/Wed at 7 AM or Tue/Thu at 6 PM. Preference?" },
            { role: "User", text: "Tue/Thu at 6 PM." },
            { role: "AI", text: "Booked. First session is on us — SMS confirmation on its way." },
        ],
        chatScript: [
            { role: "User", text: "Do you have HIIT classes on weekends?" },
            { role: "AI", text: "Yes — Sat 8 AM and Sun 9:30 AM. Reserve a spot?" },
            { role: "User", text: "Sat 8 AM." },
            { role: "AI", text: "Spot held. We'll text a reminder Friday night." },
            { role: "User", text: "Perfect, thanks." },
        ],
        pipelineSteps: ["Greet", "Match", "Book", "SMS"],
        recommendedTriggers: [
            { name: "Class no-show winback", description: "Texts members who missed a booked class to reschedule." },
            { name: "Trial-ending reminder", description: "Calls trial members 2 days before their trial ends to convert them." },
        ],
        seo: {
            title: "AI Voice & Chat for Fitness & Wellness Studios — FloGPT",
            description:
                "Studio receptionist AI that books classes, personal training and tours, sends SMS confirmations, and stays inside policy — for gyms, yoga, pilates and wellness studios.",
            keywords: ["gym AI receptionist", "yoga studio booking AI", "fitness chatbot", "personal training booking AI"],
        },
    },
    {
        key: "general_support",
        slug: slug("general_support"),
        label: "General Customer Support",
        shortLabel: "General Support",
        callWidgetType: "customer_support",
        language: "English (IN)",
        Icon: UserGroupIcon,
        accent: "from-blue-500 to-cyan-500",
        softAccent: "bg-blue-500/15",
        tagline: "Resolve 70% of repetitive support tickets — without escalating to a human.",
        description:
            "A polite, professional support agent that grounds every answer in your knowledge base, never makes up facts, and offers to transfer to a human or take a callback message when it can't help.",
        headline: "AI Customer Support for Calls & Chat",
        features: [
            "Ground every answer in your own docs, FAQs and knowledge base",
            "Never invent facts — defer or escalate when uncertain",
            "Resolve order status, returns, billing and policy questions automatically",
            "Open a high-priority support ticket on negative-sentiment calls",
            "Hand off to a human with full conversation context when needed",
        ],
        whyFloGPT: [
            { title: "Grounded, not made up", description: "Every answer cites the source doc — no hallucinated policies or fake order updates." },
            { title: "70% deflection on day one", description: "Resolves the most repetitive questions without involving your support team." },
            { title: "Sentiment-aware escalation", description: "Detects frustration and escalates to a senior agent before the relationship breaks." },
        ],
        voiceScript: [
            { role: "AI", text: "Hi, thanks for calling. How can I help today?" },
            { role: "User", text: "I haven't received order #4491." },
            { role: "AI", text: "Looking it up — shipped Tuesday, ETA tomorrow by 5 PM." },
            { role: "User", text: "Okay, thanks." },
            { role: "AI", text: "I'll text the live tracking link. Anything else?" },
        ],
        chatScript: [
            { role: "User", text: "What's your refund policy?" },
            { role: "AI", text: "30 days unused, 14 days opened. Want to start a return?" },
            { role: "User", text: "Yes — order #4491." },
            { role: "AI", text: "Return label generated and emailed. Drop at any UPS store." },
            { role: "User", text: "Great." },
        ],
        pipelineSteps: ["Greet", "Diagnose", "Resolve", "Followup"],
        recommendedTriggers: [
            { name: "Missed-call auto-callback", description: "Calls back any caller whose inbound call ended without a human answering." },
            { name: "CSAT call after ticket resolved", description: "Calls the customer when a support ticket is marked resolved to capture satisfaction." },
        ],
        seo: {
            title: "AI Customer Support Agent for Calls & Chat | FloGPT",
            description:
                "24/7 AI for inbound calls and chat. Answers from your knowledge base, deflects ~70% of tickets, and escalates negative-sentiment calls to a human.",
            keywords: ["AI customer support", "support automation", "AI helpdesk", "chatbot for customer service"],
        },
    },
    {
        key: "home_services",
        slug: slug("home_services"),
        label: "Home Services (plumbing, HVAC, cleaning, electrical)",
        shortLabel: "Home Services",
        callWidgetType: "lead_generation",
        language: "English (IN)",
        Icon: WrenchScrewdriverIcon,
        accent: "from-amber-500 to-orange-500",
        softAccent: "bg-amber-500/15",
        tagline: "Triage same-day jobs, dispatch the right tech, never miss an emergency.",
        description:
            "An intake assistant for plumbing, HVAC, cleaning and electrical that confirms the service address digit-by-digit, determines urgency, and instantly transfers gas-leak, flooding or fire calls to a human dispatcher.",
        features: [
            "Triage same-day vs scheduled vs emergency on every call",
            "Confirm the service address digit-by-digit to avoid no-shows",
            "Send technician-ETA SMS while the caller is still on the line",
            "Push jobs to ServiceTitan, Housecall Pro or any dispatch system",
            "Instantly escalate gas-leak, flooding, fire and other emergencies to a human",
        ],
        whyFloGPT: [
            { title: "Emergency-aware", description: "Detects danger keywords and routes to a human dispatcher instantly — no wait time." },
            { title: "Address-accurate", description: "Confirms the service address digit-by-digit so techs land at the right door first time." },
            { title: "Built for the truck-roll model", description: "Captures the data your dispatcher needs in 90 seconds, then sends ETA SMS automatically." },
        ],
        voiceScript: [
            { role: "AI", text: "Hi, booking a service or want a quick quote?" },
            { role: "User", text: "My kitchen drain is backed up — pretty bad." },
            { role: "AI", text: "Same-day or scheduled?" },
            { role: "User", text: "Same-day if possible." },
            { role: "AI", text: "Tech can be there in 90 minutes. What's the service address?" },
        ],
        chatScript: [
            { role: "User", text: "Do you do AC tune-ups?" },
            { role: "AI", text: "$89 spring tune-up special. Want to book?" },
            { role: "User", text: "Yes, this Saturday." },
            { role: "AI", text: "Sat 10 AM–12 PM with Carlos. Confirm address?" },
            { role: "User", text: "412 Maple Drive." },
        ],
        pipelineSteps: ["Greet", "Triage", "Dispatch", "SMS"],
        recommendedTriggers: [
            { name: "Seasonal tune-up campaign", description: "Calls past customers when their HVAC is due for spring/fall service." },
            { name: "Quote-followup call", description: "Calls back leads who got a quote but didn't book within 48 hours." },
        ],
        seo: {
            title: "AI Voice & Chat for Home Services — FloGPT",
            description:
                "Home-services AI dispatcher for plumbing, HVAC, cleaning and electrical companies. Triages emergencies, captures jobs, sends ETA SMS and pushes to ServiceTitan / Housecall Pro.",
            keywords: ["plumbing AI dispatcher", "HVAC AI receptionist", "home services chatbot", "service business AI"],
        },
    },
    {
        key: "hospitality",
        slug: slug("hospitality"),
        label: "Hospitality (hotels, vacation rentals)",
        shortLabel: "Hospitality",
        callWidgetType: "customer_support",
        language: "English (IN)",
        Icon: BuildingLibraryIcon,
        accent: "from-indigo-500 to-purple-500",
        softAccent: "bg-indigo-500/15",
        tagline: "AI guest communication for hotels and vacation rentals — every call and message, in every language.",
        description:
            "An AI guest communication tool for hotels, short-term and vacation rentals: a courteous front-desk assistant that handles booking enquiries, check-in and move-in questions, and concierge requests across voice, chat, SMS and WhatsApp. It confirms reservation details before changing anything, never promises comps or upgrades, and escalates upset guests to a manager — in 16 languages.",
        features: [
            "Automate guest communication across voice, chat, SMS and WhatsApp 24/7",
            "Handle booking modifications, room changes and concierge requests for hotels and short-term rentals",
            "Answer check-in, move-in and self-check-in questions instantly",
            "Confirm reservation details before any change to prevent ledger mistakes",
            "Email guest receipts and itinerary updates automatically",
            "Push changes to Cloudbeds, Mews, Hostaway or any PMS via webhook",
            "Escalate upset guests (raised voice, repeated complaints) to a manager",
        ],
        whyFloGPT: [
            { title: "Multi-lingual concierge", description: "16 languages out of the box — including Hindi, Tamil and 7 other Indic languages." },
            { title: "Manager-safe", description: "Never promises comps, refunds or upgrades — always defers to a human for goodwill." },
            { title: "Sentiment-aware", description: "Detects upset guests and escalates to a manager before the bad review hits TripAdvisor." },
        ],
        voiceScript: [
            { role: "AI", text: "Thank you for calling Cedar Inn — how may I help?" },
            { role: "User", text: "I'd like to extend my stay one night, room 412." },
            { role: "AI", text: "Confirming — checkout pushed Friday to Saturday. Same room?" },
            { role: "User", text: "Yes." },
            { role: "AI", text: "All set. Updated invoice has been emailed." },
        ],
        chatScript: [
            { role: "User", text: "Can I check in early Saturday?" },
            { role: "AI", text: "Early check-in at 12 PM is $40, or after 3 PM is free. Which?" },
            { role: "User", text: "12 PM, please." },
            { role: "AI", text: "Added. We'll have your key ready at the desk." },
            { role: "User", text: "Thanks." },
        ],
        pipelineSteps: ["Greet", "Verify", "Update", "Email"],
        recommendedTriggers: [
            { name: "Pre-arrival concierge call", description: "Calls guests 24 hours before check-in to confirm and offer add-ons." },
            { name: "Post-stay review request", description: "Calls or texts guests one day after checkout to invite a review." },
        ],
        seo: {
            title: "Multilingual AI Voice & Chat for Hotels & Vacation Rentals — FloGPT",
            description:
                "Multilingual AI voice & chat agent for hotels, short-term and vacation rentals — automate front-desk calls, bookings, check-in and concierge requests across voice, chat, SMS and WhatsApp in 16 languages, with PMS webhooks and sentiment-aware escalation.",
            keywords: [
                "AI guest communication tools",
                "multilingual AI voice agent for hotels",
                "AI voice automation for hotels",
                "hotel AI voice bot for front desk",
                "AI voice assistant for hotel front desk",
                "AI chatbot for vacation rentals",
                "AI voice agent for hospitality",
                "short-term rental guest communication",
                "hotel AI receptionist",
                "vacation rental chatbot",
                "property management AI",
            ],
        },
    },
    {
        key: "insurance",
        slug: slug("insurance"),
        label: "Insurance",
        shortLabel: "Insurance",
        callWidgetType: "lead_generation",
        language: "English (IN)",
        Icon: BuildingOffice2Icon,
        accent: "from-sky-500 to-blue-500",
        softAccent: "bg-sky-500/15",
        tagline: "Capture quote leads, route active claims, never quote a rate.",
        description:
            "An insurance intake assistant that captures the line of insurance, current carrier and renewal date — never quotes a rate or guarantees approval, and instantly transfers active claims with injury or damage in progress.",
        features: [
            "Capture line (auto, home, life, health, commercial), carrier and renewal date",
            "Never quote rates or guarantee approval — collect and hand off to a licensed agent",
            "Push leads to AMS360, Applied Epic or any agency-management system",
            "Instantly transfer active-claim callers (injury, accident, damage)",
            "Send quote-request SMS confirmations with the agent's callback ETA",
        ],
        whyFloGPT: [
            { title: "Compliance-first", description: "Never quotes rates or makes binding promises — every quote routes to a licensed agent." },
            { title: "Claims-aware", description: "Detects active-claim language (accident, injury, totaled) and transfers to a human instantly." },
            { title: "Renewal-driven", description: "Captures the renewal date so your team can re-engage at exactly the right moment." },
        ],
        voiceScript: [
            { role: "AI", text: "Hi, calling about a quote, claim, or policy change?" },
            { role: "User", text: "Auto quote — switching from Geico." },
            { role: "AI", text: "Got it. Current coverage limits and renewal date?" },
            { role: "User", text: "100/300/100, renewing March 15." },
            { role: "AI", text: "A licensed agent will call you within an hour with options." },
        ],
        chatScript: [
            { role: "User", text: "Do you cover rideshare drivers?" },
            { role: "AI", text: "Yes — rideshare endorsement available. Add to your auto policy?" },
            { role: "User", text: "How much extra?" },
            { role: "AI", text: "Typically $15–25 per month. Want a quote?" },
            { role: "User", text: "Yes please." },
        ],
        pipelineSteps: ["Greet", "Capture", "Quote", "Agent"],
        recommendedTriggers: [
            { name: "Renewal-window outbound call", description: "Calls policyholders 30 days before renewal to quote alternatives." },
            { name: "Quote-followup call", description: "Calls back prospects who requested a quote but didn't bind." },
        ],
        seo: {
            title: "AI Voice & Chat for Insurance Agencies — FloGPT",
            description:
                "Insurance AI intake agent for auto, home, life, health and commercial. Captures quote leads, routes active claims, and pushes data to AMS360 / Applied Epic — never quotes a rate.",
            keywords: ["insurance AI agent", "agency AI receptionist", "insurance chatbot", "insurance claims AI"],
        },
    },
    {
        key: "legal",
        slug: slug("legal"),
        label: "Legal",
        shortLabel: "Legal",
        callWidgetType: "customer_support",
        language: "English (IN)",
        Icon: BriefcaseIcon,
        accent: "from-slate-500 to-zinc-500",
        softAccent: "bg-slate-500/15",
        tagline: "Run intake, flag conflicts, never give legal advice.",
        description:
            "A legal-intake assistant that captures the case type and conflicts-check name, never provides legal advice, and flags time-sensitive matters (court date this week, arrest, restraining order) for immediate attorney transfer.",
        features: [
            "Capture matter type and conflicts-check name on every intake",
            "Never provide legal advice — explicitly defer to the attorney",
            "Flag time-sensitive matters (court dates, arrests) for immediate transfer",
            "Email a structured intake form right after the call",
            "Push case data into Clio, MyCase or your conflicts-check system",
        ],
        whyFloGPT: [
            { title: "Stays in lane", description: "Trained to never offer opinions, strategies or legal advice — always defers to the attorney." },
            { title: "Conflicts-check first", description: "Captures the opposing-party name during intake so your firm can vet representation." },
            { title: "Urgency-routing", description: "Court-tomorrow, arrest, restraining-order matters get routed to the on-call attorney instantly." },
        ],
        voiceScript: [
            { role: "AI", text: "Thanks for calling — I'm an intake assistant. What's the matter regarding?" },
            { role: "User", text: "I was rear-ended and need a personal-injury attorney." },
            { role: "AI", text: "Understood. Any injuries or police report?" },
            { role: "User", text: "Whiplash, and yes there's a report." },
            { role: "AI", text: "Booking a free consult today. Attorney calls within an hour." },
        ],
        chatScript: [
            { role: "User", text: "I need help with a landlord dispute." },
            { role: "AI", text: "We handle landlord/tenant. Brief details — and we'll match an attorney." },
            { role: "User", text: "Security deposit hasn't been returned, 60 days late." },
            { role: "AI", text: "Strong case. Free 15-min consult slots open at 2 PM and 4 PM today." },
            { role: "User", text: "2 PM works." },
        ],
        pipelineSteps: ["Greet", "Intake", "Match", "Schedule"],
        recommendedTriggers: [
            { name: "Court-date reminder", description: "Calls clients 48 hours before their court date with the time and address." },
            { name: "Consult-followup call", description: "Calls back prospects who took an intake but didn't sign the engagement letter." },
        ],
        seo: {
            title: "AI Voice & Chat for Law Firms — FloGPT",
            description:
                "Law-firm AI intake assistant. Captures matter and conflicts-check name, never gives legal advice, and routes urgent matters to the on-call attorney instantly.",
            keywords: ["law firm AI intake", "legal chatbot", "attorney AI assistant", "conflicts check AI"],
        },
    },
    {
        key: "logistics",
        slug: slug("logistics"),
        label: "Logistics",
        shortLabel: "Logistics",
        callWidgetType: "customer_support",
        language: "English (IN)",
        Icon: CubeIcon,
        accent: "from-cyan-500 to-blue-500",
        softAccent: "bg-cyan-500/15",
        tagline: "Track shipments, change deliveries, file damage claims — at scale.",
        description:
            "A polite support agent for logistics and shipping companies that captures the tracking number first, never promises specific delivery dates without checking the system, and sends the photo-upload link by SMS for damaged packages.",
        features: [
            "Surface tracking, delivery and pickup status from your TMS",
            "Handle delivery-change requests with automated SMS confirmation",
            "Open damaged-package claims with a photo-upload link sent by SMS",
            "Push delivery changes and pickup requests to your TMS via webhook",
            "Escalate lost-shipment and supervisor requests to a human",
        ],
        whyFloGPT: [
            { title: "Tracking-first", description: "Always captures the tracking number first so you don't waste callers' time on dead-end calls." },
            { title: "Built for high call volume", description: "Handles 1000s of inbound calls in parallel — no menu-tree, no hold music." },
            { title: "Photo-upload for damage", description: "Sends a one-tap photo-upload SMS so your team gets the evidence they need to investigate." },
        ],
        voiceScript: [
            { role: "AI", text: "Thanks for calling. Tracking, delivery, or pickup?" },
            { role: "User", text: "Tracking — number 1Z999AA10123456784." },
            { role: "AI", text: "Out for delivery, ETA 4:30 PM today." },
            { role: "User", text: "Can you change it to my office address?" },
            { role: "AI", text: "Yes — texting you a delivery-change link to confirm." },
        ],
        chatScript: [
            { role: "User", text: "My package was marked delivered but never arrived." },
            { role: "AI", text: "Sorry to hear. Let's start a missing-package claim." },
            { role: "User", text: "Okay." },
            { role: "AI", text: "Photo-upload link sent. We'll investigate within 24 hours." },
            { role: "User", text: "Thanks." },
        ],
        pipelineSteps: ["Greet", "Lookup", "Update", "SMS"],
        recommendedTriggers: [
            { name: "Out-for-delivery proactive call", description: "Calls the recipient when their package goes out for delivery to confirm presence." },
            { name: "Damage-claim follow-up", description: "Calls claimants 24 hours after a damage claim to confirm photo upload." },
        ],
        seo: {
            title: "AI Voice & Chat for Logistics & Shipping — FloGPT",
            description:
                "Logistics AI agent for tracking, delivery changes, pickup requests and damaged-package claims. Pushes updates to your TMS and sends photo-upload SMS automatically.",
            keywords: ["logistics AI agent", "shipping chatbot", "tracking AI", "TMS AI integration"],
        },
    },
    {
        key: "real_estate",
        slug: slug("real_estate"),
        label: "Real Estate",
        shortLabel: "Real Estate",
        callWidgetType: "lead_generation",
        language: "English (IN)",
        Icon: HomeModernIcon,
        accent: "from-violet-500 to-indigo-500",
        softAccent: "bg-violet-500/15",
        tagline: "Qualify buyers and sellers, book showings, push leads to your CRM.",
        description:
            "A real-estate intake assistant that qualifies whether the caller is a buyer or seller, gathers budget and timeline, books a follow-up with an agent, and stays inside fair-housing rules — never asking about race, religion or family status.",
        features: [
            "Qualify buyer / seller / renter intent and capture budget and timeline",
            "Send matching MLS listings by SMS while the lead is still on the line",
            "Push leads to Follow Up Boss, kvCORE or your CRM via webhook",
            "Book showings and listing-presentation appointments into your team calendar",
            "Schedule a 24-hour outbound follow-up call if no booking happens",
        ],
        whyFloGPT: [
            { title: "Fair-housing compliant", description: "Trained to never ask about race, religion, family status, national origin or disability." },
            { title: "Listing-aware", description: "Connects to your MLS feed so it can answer questions on specific listings in real time." },
            { title: "Pre-approved-friendly", description: "Captures financing status during qualification so your agent knows the lead's seriousness." },
        ],
        voiceScript: [
            { role: "AI", text: "Hi, looking to buy, sell, or asking about a listing?" },
            { role: "User", text: "Buying — interested in 412 Maple Drive." },
            { role: "AI", text: "Pre-approved for financing?" },
            { role: "User", text: "Yes, up to $650k." },
            { role: "AI", text: "Booking a showing Saturday 11 AM. Agent confirms by SMS." },
        ],
        chatScript: [
            { role: "User", text: "Any 3-bed homes in Kensington under $800k?" },
            { role: "AI", text: "Five active. Want me to send the top three with photos?" },
            { role: "User", text: "Yes." },
            { role: "AI", text: "Sent. Two have open houses Sunday — interested?" },
            { role: "User", text: "Both, please." },
        ],
        pipelineSteps: ["Greet", "Qualify", "Book", "CRM"],
        recommendedTriggers: [
            { name: "Open-house follow-up", description: "Calls visitors 24 hours after an open house to gauge interest." },
            { name: "Listing-question callback", description: "Calls back leads who asked a listing question via web form." },
        ],
        seo: {
            title: "AI Voice & Chat for Real Estate — FloGPT",
            description:
                "Real-estate AI agent for buyer/seller qualification, listing inquiries, showing booking and CRM push. Fair-housing compliant out of the box, integrates with FUB / kvCORE.",
            keywords: ["real estate AI", "realtor chatbot", "buyer lead AI", "MLS AI assistant"],
        },
    },
    {
        key: "restaurants",
        slug: slug("restaurants"),
        label: "Restaurants",
        shortLabel: "Restaurants",
        callWidgetType: "appointment_booking",
        language: "English (IN)",
        Icon: BuildingStorefrontIcon,
        accent: "from-pink-500 to-rose-500",
        softAccent: "bg-pink-500/15",
        tagline: "Take reservations and waitlist signups while your host runs the floor.",
        description:
            "A friendly host taking reservations — confirming party size, date and time back to the caller, asking about dietary restrictions and special occasions, and deferring private events to the events manager.",
        features: [
            "Take reservations, modifications and cancellations 24/7",
            "Capture special occasions and dietary restrictions for the kitchen",
            "Push reservations to OpenTable, Resy, SevenRooms or any system",
            "Send post-meal review-request SMS automatically",
            "Defer private-event and buyout requests to the events manager",
        ],
        whyFloGPT: [
            { title: "Host-friendly", description: "Picks up the line during the dinner rush so your host can stay on the floor." },
            { title: "Special-occasion aware", description: "Captures anniversaries, birthdays and dietary needs so your team can wow the guest." },
            { title: "Review-request automation", description: "Sends a post-meal SMS at the right moment to drive 5-star reviews." },
        ],
        voiceScript: [
            { role: "AI", text: "Hi! Reservation or a question?" },
            { role: "User", text: "Reservation — party of 4, this Saturday." },
            { role: "AI", text: "6:30 PM or 8:00 PM?" },
            { role: "User", text: "8 PM. It's an anniversary." },
            { role: "AI", text: "Booked. We'll have a candle on the table — confirmation SMS sent." },
        ],
        chatScript: [
            { role: "User", text: "Do you take walk-ins on Friday?" },
            { role: "AI", text: "Yes, but the wait is usually 45 minutes. Add you to the waitlist?" },
            { role: "User", text: "Yes — for two." },
            { role: "AI", text: "Added. We'll text when your table is 10 minutes out." },
            { role: "User", text: "Great." },
        ],
        pipelineSteps: ["Greet", "Capture", "Reserve", "SMS"],
        recommendedTriggers: [
            { name: "Reservation-day reminder", description: "Calls or texts guests the morning of their reservation to reduce no-shows." },
            { name: "Slow-night winback", description: "Calls regulars on a known-slow night to invite them in with a special offer." },
        ],
        seo: {
            title: "AI Voice & Chat for Restaurants — FloGPT",
            description:
                "Restaurant AI host that takes reservations and waitlist signups 24/7, captures special occasions, and pushes bookings to OpenTable / Resy / SevenRooms — with post-meal review requests.",
            keywords: ["restaurant AI host", "reservation AI", "OpenTable AI", "restaurant chatbot"],
        },
    },
    {
        key: "retail",
        slug: slug("retail"),
        label: "Retail",
        shortLabel: "Retail",
        callWidgetType: "customer_support",
        language: "English (IN)",
        Icon: ShoppingBagIcon,
        accent: "from-fuchsia-500 to-pink-500",
        softAccent: "bg-fuchsia-500/15",
        tagline: "Resolve order, return and product questions — without shrinking your support team.",
        description:
            "A polite customer-support agent for retail brands. Surfaces order status, processes returns, never promises a refund or store credit, and creates a high-priority ticket on every refund request.",
        features: [
            "Resolve order status, return and product questions automatically",
            "Push to your OMS / Shopify, with a high-priority ticket on every refund request",
            "Recover abandoned checkouts with proactive outbound calls",
            "Surface size charts, fit guidance and style advice from your knowledge base",
            "Escalate refund and chargeback callers to a human supervisor",
        ],
        whyFloGPT: [
            { title: "Refund-safe", description: "Never promises a refund or store credit on the call — always creates a ticket for human review." },
            { title: "Cart-recovery built in", description: "Auto-calls Shopify customers who abandoned checkout above your minimum cart value." },
            { title: "Knowledge-base grounded", description: "Surfaces size charts, fit notes and product details from your own docs — never invents specs." },
        ],
        voiceScript: [
            { role: "AI", text: "Thanks for calling — what can I help with today?" },
            { role: "User", text: "My shoes arrived damaged — order #8821." },
            { role: "AI", text: "I'm so sorry. Refund or replacement?" },
            { role: "User", text: "Replacement." },
            { role: "AI", text: "Done. New pair ships today and a return label is on its way." },
        ],
        chatScript: [
            { role: "User", text: "Is this jacket true to size?" },
            { role: "AI", text: "Runs slightly small — most reviewers size up. Want size charts?" },
            { role: "User", text: "Yes." },
            { role: "AI", text: "Sent. Free returns within 30 days." },
            { role: "User", text: "Thanks." },
        ],
        pipelineSteps: ["Greet", "Lookup", "Resolve", "Replace"],
        recommendedTriggers: [
            { name: "Abandoned-checkout recovery", description: "Calls Shopify customers who abandoned checkout above the minimum cart value." },
            { name: "Post-purchase satisfaction call", description: "Calls customers 7 days after delivery to ask about the product and capture review intent." },
        ],
        seo: {
            title: "AI Voice & Chat for Retail Brands — FloGPT",
            description:
                "Retail AI customer support for orders, returns, product questions and abandoned-checkout recovery. Integrates with Shopify and any OMS — refund-safe, knowledge-base grounded.",
            keywords: ["retail AI support", "ecommerce AI agent", "Shopify chatbot", "abandoned checkout AI"],
        },
    },
];

export const INDUSTRY_USE_CASES_BY_KEY: Record<string, IndustryUseCase> = INDUSTRY_USE_CASES.reduce(
    (acc, ind) => {
        acc[ind.key] = ind;
        return acc;
    },
    {} as Record<string, IndustryUseCase>,
);

export const INDUSTRY_USE_CASES_BY_SLUG: Record<string, IndustryUseCase> = INDUSTRY_USE_CASES.reduce(
    (acc, ind) => {
        acc[ind.slug] = ind;
        return acc;
    },
    {} as Record<string, IndustryUseCase>,
);

/**
 * Pick a few related industries based on the same widget type, falling back
 * to alphabetical order if none match. Used in the per-page footer.
 */
export function getRelatedIndustries(industry: IndustryUseCase, limit = 3): IndustryUseCase[] {
    const sameType = INDUSTRY_USE_CASES.filter(
        (i) => i.key !== industry.key && i.callWidgetType === industry.callWidgetType,
    );
    const others = INDUSTRY_USE_CASES.filter(
        (i) => i.key !== industry.key && i.callWidgetType !== industry.callWidgetType,
    );
    return [...sameType, ...others].slice(0, limit);
}
